import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '@/hooks/useWallet';
import { useContract } from '@/hooks/useContract';
import { useToast } from '@/contexts/ToastContext';
import { CharacterClass, CLASS_INFO } from '@/lib/types/game';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { ClassSprite } from '@/components/game/PixelSprites';

// Retro pixel-art style class icons (SVG inline)
const ClassIcon: React.FC<{ classType: CharacterClass; size?: number }> = ({ classType, size = 20 }) => {
  const icons: Record<CharacterClass, React.ReactElement> = {
    [CharacterClass.FIGHTER]: (
      <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
        {/* Sword */}
        <rect x="7" y="1" width="2" height="10" fill="#C0C0C0" />
        <rect x="5" y="10" width="6" height="2" fill="#8B4513" />
        <rect x="6" y="12" width="4" height="2" fill="#8B4513" />
        <rect x="7" y="0" width="2" height="2" fill="#FFD700" />
      </svg>
    ),
    [CharacterClass.THIEF]: (
      <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
        {/* Dagger */}
        <rect x="7" y="2" width="2" height="8" fill="#C0C0C0" />
        <rect x="5" y="9" width="6" height="1" fill="#8B4513" />
        <rect x="6" y="10" width="4" height="3" fill="#4B0082" />
        <rect x="7" y="1" width="2" height="2" fill="#C0C0C0" />
      </svg>
    ),
    [CharacterClass.MONK]: (
      <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
        {/* Fist */}
        <rect x="4" y="4" width="8" height="8" fill="#FFDBAC" />
        <rect x="4" y="5" width="2" height="6" fill="#FFDBAC" />
        <rect x="10" y="5" width="2" height="6" fill="#FFDBAC" />
        <rect x="3" y="6" width="2" height="4" fill="#FFDBAC" />
        <rect x="5" y="12" width="6" height="2" fill="#FFDBAC" />
      </svg>
    ),
    [CharacterClass.RED_MAGE]: (
      <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
        {/* Hat with feather */}
        <rect x="4" y="6" width="8" height="6" fill="#DC143C" />
        <rect x="5" y="4" width="6" height="3" fill="#DC143C" />
        <rect x="6" y="2" width="4" height="3" fill="#DC143C" />
        <rect x="10" y="1" width="2" height="4" fill="#FFF" />
        <rect x="4" y="11" width="8" height="2" fill="#FFD700" />
      </svg>
    ),
    [CharacterClass.WHITE_MAGE]: (
      <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
        {/* Staff with cross */}
        <rect x="7" y="2" width="2" height="12" fill="#8B4513" />
        <rect x="5" y="2" width="6" height="2" fill="#87CEEB" />
        <rect x="6" y="1" width="4" height="4" fill="#87CEEB" />
        <rect x="7" y="0" width="2" height="2" fill="#FFF" />
      </svg>
    ),
    [CharacterClass.BLACK_MAGE]: (
      <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
        {/* Staff with orb */}
        <rect x="7" y="4" width="2" height="10" fill="#8B4513" />
        <rect x="5" y="1" width="6" height="4" fill="#FF4500" />
        <rect x="6" y="2" width="4" height="2" fill="#FFD700" />
        <rect x="4" y="3" width="8" height="1" fill="#191970" />
      </svg>
    ),
  };

  return icons[classType];
};

interface SlotInfo {
  slots: boolean[];
  current: number;
}

const NewGame: React.FC = () => {
  const navigate = useNavigate();
  const { isConnected } = useWallet();
  const { createGame, getSlotInfo } = useContract();
  const toast = useToast();

  const [slotInfo, setSlotInfo] = useState<SlotInfo | null>(null);
  const [isLoadingSlots, setIsLoadingSlots] = useState(true);
  const [selectedClasses, setSelectedClasses] = useState<(CharacterClass | null)[]>([null, null, null, null]);
  const [isCreating, setIsCreating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Redirect if not connected
  useEffect(() => {
    if (!isConnected) {
      navigate('/');
    }
  }, [isConnected, navigate]);

  // Load slot info
  useEffect(() => {
    const loadSlots = async () => {
      setIsLoadingSlots(true);
      try {
        const info = await getSlotInfo();
        setSlotInfo(info);
      } catch (error) {
        console.error('Failed to load slots:', error);
      } finally {
        setIsLoadingSlots(false);
      }
    };

    if (isConnected) {
      loadSlots();
    }
  }, [isConnected, getSlotInfo]);

  // Find the first available slot
  const getFirstAvailableSlot = (): number | null => {
    if (!slotInfo) return 0; // Default to slot 0 if no info yet
    const index = slotInfo.slots.findIndex(active => !active);
    return index === -1 ? null : index;
  };

  const handleClassSelect = (index: number, classType: CharacterClass) => {
    const newClasses = [...selectedClasses];
    newClasses[index] = classType;
    setSelectedClasses(newClasses);
  };

  const canCreateGame = selectedClasses.every((c) => c !== null);
  const availableSlot = getFirstAvailableSlot();
  const allSlotsFull = availableSlot === null;

  const handleCreateGame = async () => {
    if (!canCreateGame || availableSlot === null) {
      toast.error('Please select a class for all 4 characters');
      return;
    }

    setIsCreating(true);
    setShowConfirm(false);

    try {
      const classes = selectedClasses as [CharacterClass, CharacterClass, CharacterClass, CharacterClass];
      const result = await createGame(availableSlot, classes);

      if (result.success) {
        toast.success('Your adventure begins!');
        navigate('/game');
      } else {
        toast.error(result.error || 'Failed to create game. Please try again.');
        setIsCreating(false);
      }
    } catch (error) {
      console.error('Failed to create game:', error);
      toast.error('Failed to create game. Please try again.');
      setIsCreating(false);
    }
  };

  const classOptions = [
    { class: CharacterClass.FIGHTER, color: 'bg-red-600' },
    { class: CharacterClass.THIEF, color: 'bg-purple-600' },
    { class: CharacterClass.MONK, color: 'bg-orange-600' },
    { class: CharacterClass.RED_MAGE, color: 'bg-pink-600' },
    { class: CharacterClass.WHITE_MAGE, color: 'bg-blue-400' },
    { class: CharacterClass.BLACK_MAGE, color: 'bg-indigo-700' },
  ];

  if (isLoadingSlots) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-b from-[#1a1a2e] to-[#0f0e17] flex items-center justify-center">
        <LoadingSpinner text="Loading..." />
      </div>
    );
  }

  // Show error if all slots are full
  if (allSlotsFull) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-b from-[#1a1a2e] to-[#0f0e17] p-4">
        <div className="max-w-2xl mx-auto py-8">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-pixel-lg text-[#e94560] text-shadow">New Game</h1>
            <Button variant="secondary" onClick={() => navigate('/')}>
              Back
            </Button>
          </div>

          <Card className="bg-red-900/20 border-red-500">
            <div className="text-center py-8">
              <p className="text-game text-xl text-red-400 mb-4">
                All Save Slots Full
              </p>
              <p className="text-game text-sm text-gray-400 mb-6">
                You have 8 active save files. Delete an existing save from the Load Game menu to create a new adventure.
              </p>
              <Button variant="secondary" onClick={() => navigate('/')}>
                Return to Title
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Party Creation
  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-[#1a1a2e] to-[#0f0e17] p-6">
      <div className="max-w-6xl mx-auto py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <h1 className="text-pixel-lg text-[#e94560] text-shadow">Create Your Party</h1>
          <Button variant="secondary" size="md" onClick={() => navigate('/')}>
            Back
          </Button>
        </div>

        {/* Instructions */}
        <Card className="mb-10 card-spacious">
          <p className="text-game text-xl mb-4 leading-relaxed">
            Choose your party composition carefully. Each class has unique strengths and weaknesses.
            A balanced party is recommended for beginners.
          </p>
          <p className="text-game text-lg text-[#e94560]">
            Classic Party: Fighter, Thief, White Mage, Black Mage
          </p>
        </Card>

        {/* Party Preview - shows selected characters */}
        <Card className="mb-10 card-normal">
          <div className="flex items-center justify-center gap-8">
            {selectedClasses.map((classType, index) => (
              <div key={index} className="flex flex-col items-center">
                <div className="w-16 h-16 flex items-center justify-center mb-2">
                  {classType !== null ? (
                    <ClassSprite classId={classType} size={64} />
                  ) : (
                    <div className="w-14 h-14 border-2 border-dashed border-[#533483] rounded flex items-center justify-center">
                      <span className="text-game text-2xl text-[#533483]">?</span>
                    </div>
                  )}
                </div>
                <span className="text-pixel-xs text-gray-400">
                  {classType !== null ? CLASS_INFO[classType].name : `Slot ${index + 1}`}
                </span>
              </div>
            ))}
          </div>
        </Card>

        {/* Party Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
          {selectedClasses.map((selectedClass, index) => {
            // Get classes already selected by OTHER slots (not this one)
            const otherSelectedClasses = selectedClasses
              .filter((_, i) => i !== index)
              .filter((c): c is CharacterClass => c !== null);

            return (
              <Card key={index} title={`Character ${index + 1}`} className="card-spacious">
                <div className="space-y-3">
                  {classOptions.map(({ class: classType, color }) => {
                    const isSelected = selectedClass === classType;
                    const isUsedByOther = otherSelectedClasses.includes(classType);
                    const isDisabled = isUsedByOther && !isSelected;

                    return (
                      <button
                        key={classType}
                        onClick={() => !isDisabled && handleClassSelect(index, classType)}
                        disabled={isDisabled}
                        className={`
                          w-full class-btn ff-border text-left
                          ${isSelected ? `${color} selected` : ''}
                          ${isDisabled ? 'opacity-40 cursor-not-allowed bg-[#1a1a2e]' : !isSelected ? 'bg-[#0f3460] hover:bg-[#16213e]' : ''}
                        `}
                      >
                        <div className="flex items-center gap-3">
                          <ClassIcon classType={classType} size={24} />
                          <span className="text-pixel-sm">{CLASS_INFO[classType].name}</span>
                          {isUsedByOther && !isSelected && (
                            <span className="text-game text-xs text-gray-500 ml-auto">In Use</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </Card>
            );
          })}
        </div>

        {/* Selected classes details with stats - always visible */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {selectedClasses.map((classType, index) => {
            if (classType === null) {
              // Placeholder for unselected slot
              return (
                <Card key={index} className="bg-[#1a1a2e]/50 card-normal border-dashed border-[#533483]/50">
                  <h3 className="text-pixel-sm text-gray-500 mb-3">Character {index + 1}</h3>
                  <p className="text-game text-base text-gray-600 leading-relaxed mb-4">
                    Select a class above to view stats
                  </p>
                  <div className="border-t border-[#533483]/30 pt-3">
                    <div className="grid grid-cols-2 gap-2 text-game text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">HP</span>
                        <span className="text-gray-600">--</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">MP</span>
                        <span className="text-gray-600">--</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">STR</span>
                        <span className="text-gray-600">--</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">AGI</span>
                        <span className="text-gray-600">--</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">INT</span>
                        <span className="text-gray-600">--</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">VIT</span>
                        <span className="text-gray-600">--</span>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            }
            const info = CLASS_INFO[classType];
            return (
              <Card key={index} className="bg-[#0f3460]/50 card-normal">
                <h3 className="text-pixel-sm text-[#e94560] mb-3">{info.name}</h3>
                <p className="text-game text-base leading-relaxed mb-4">{info.description}</p>
                <div className="border-t border-[#533483]/50 pt-3">
                  <div className="grid grid-cols-2 gap-2 text-game text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">HP</span>
                      <span className="text-[#00ff00]">{info.baseHP}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">MP</span>
                      <span className="text-[#00aaff]">{info.baseMP}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">STR</span>
                      <span className="text-[#ff6644]">{info.baseStr}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">AGI</span>
                      <span className="text-[#44ff44]">{info.baseAgi}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">INT</span>
                      <span className="text-[#aa88ff]">{info.baseInt}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">VIT</span>
                      <span className="text-[#ffaa44]">{info.baseVit}</span>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Action buttons */}
        <div className="flex justify-center gap-6">
          <Button
            variant="primary"
            size="lg"
            onClick={() => setShowConfirm(true)}
            disabled={!canCreateGame}
            isLoading={isCreating}
          >
            {isCreating ? 'Creating Game...' : 'Begin Adventure'}
          </Button>
          <Button
            variant="secondary"
            size="lg"
            onClick={() => setSelectedClasses([null, null, null, null])}
            disabled={isCreating}
          >
            Reset
          </Button>
        </div>
      </div>

      {/* Confirmation modal */}
      <Modal isOpen={showConfirm} onClose={() => setShowConfirm(false)} title="Confirm Party">
        <div className="modal-section">
          <p className="text-game text-xl mb-6">
            Are you ready to begin your adventure with this party?
          </p>
          <div className="space-y-3 mb-8">
            {selectedClasses.map((classType, index) => {
              if (classType === null) return null;
              return (
                <div key={index} className="ff-border px-4 py-3 bg-[#0f3460] flex items-center gap-3">
                  <span className="text-[#ffd700] text-game text-lg">{index + 1}.</span>
                  <span className="text-pixel-sm">
                    {CLASS_INFO[classType].name}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="flex justify-end gap-4">
            <Button variant="secondary" size="md" onClick={() => setShowConfirm(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="md" onClick={handleCreateGame}>
              Confirm
            </Button>
          </div>
        </div>
      </Modal>

      {/* Full-screen loading overlay when creating game */}
      {isCreating && (
        <LoadingSpinner
          size="lg"
          text="Creating your adventure on the blockchain... Please confirm in your wallet."
          fullScreen
        />
      )}
    </div>
  );
};

export default NewGame;
